import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ReactFlowCanvasNode } from '../../hooks/useYCanvas'
import { cn } from '../../lib/utils'
import { createNadDebug } from '../../utils/debugLog'
import { QuestionCard, type QuestionCardProps, type QuestionOption } from './QuestionCard'

const questionBatchLog = createNadDebug('dw:chat:question-batch')

export interface BatchedQuestionItem {
  id: string
  question: string
  options: QuestionOption[]
  nodeRef?: QuestionCardProps['nodeRef']
  byomConfiguration?: QuestionCardProps['byomConfiguration']
  selectedOption?: string
  allowCustomInput?: boolean
  customText?: string
  customSelected?: boolean
  skipped?: boolean
}

interface BatchedQuestionCardProps {
  questions: BatchedQuestionItem[]
  onSelect?: (questionId: string, option: string) => void
  onCustomTextChange?: (questionId: string, text: string) => void
  onCustomInputFocus?: (questionId: string) => void
  onSkip?: (questionId: string) => void
  onSubmit?: () => void
  disabled?: boolean
  canvasNodes?: ReactFlowCanvasNode[]
}

function hasAnswer(question: BatchedQuestionItem): boolean {
  return Boolean(question.selectedOption || question.customText?.trim())
}

function isComplete(question: BatchedQuestionItem): boolean {
  return hasAnswer(question) || Boolean(question.skipped)
}

export function BatchedQuestionCard({
  questions,
  onSelect,
  onCustomTextChange,
  onCustomInputFocus,
  onSkip,
  onSubmit,
  disabled = false,
  canvasNodes,
}: BatchedQuestionCardProps) {
  const { t } = useTranslation('chat')
  const [currentIndex, setCurrentIndex] = useState(0)
  const questionIds = useMemo(() => questions.map((question) => question.id).join('\u0000'), [questions])
  const total = questions.length
  const currentQuestion = questions[currentIndex]
  const canSubmit = questions.slice(0, -1).every(isComplete)

  useEffect(() => {
    setCurrentIndex(0)
  }, [questionIds])

  useEffect(() => {
    if (currentIndex >= total && total > 0) setCurrentIndex(total - 1)
  }, [currentIndex, total])

  const goTo = useCallback((index: number) => {
    if (index < 0 || index >= total) return
    setCurrentIndex(index)
    questionBatchLog({
      event: 'question-batch:navigate',
      current: currentIndex + 1,
      next: index + 1,
      total,
      readOnly: disabled,
    })
  }, [currentIndex, disabled, total])

  const advanceAfterAnswer = useCallback((questionId: string) => {
    const nextUnansweredIndex = questions.findIndex(
      (question, index) => index > currentIndex && question.id !== questionId && !isComplete(question),
    )
    if (nextUnansweredIndex >= 0) {
      setCurrentIndex(nextUnansweredIndex)
      return
    }
    const wrappedUnansweredIndex = questions.findIndex(
      (question) => question.id !== questionId && !isComplete(question),
    )
    if (wrappedUnansweredIndex >= 0) {
      setCurrentIndex(wrappedUnansweredIndex)
      return
    }
    if (currentIndex < total - 1) setCurrentIndex(currentIndex + 1)
  }, [currentIndex, questions, total])

  if (!currentQuestion || total < 2) return null

  const pager = (
    <div className="flex shrink-0 select-none items-center gap-1">
      <button
        type="button"
        onClick={() => goTo(currentIndex - 1)}
        disabled={currentIndex === 0}
        aria-label={t('questionBatch.previousQuestion')}
        className={cn(
          'rounded-lg p-1 text-muted-foreground transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
          currentIndex === 0
            ? 'cursor-not-allowed opacity-30'
            : 'hover:bg-surface-3 hover:text-foreground',
        )}
      >
        <ChevronLeft className="h-4 w-4" strokeWidth={2} />
      </button>
      <span
        data-testid="question-batch-counter"
        className="min-w-12 text-center text-sm tabular-nums text-muted-foreground"
      >
        {t('questionBatch.counter', { current: currentIndex + 1, total })}
      </span>
      <button
        type="button"
        onClick={() => goTo(currentIndex + 1)}
        disabled={currentIndex === total - 1}
        aria-label={t('questionBatch.nextQuestion')}
        className={cn(
          'rounded-lg p-1 text-muted-foreground transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20',
          currentIndex === total - 1
            ? 'cursor-not-allowed opacity-30'
            : 'hover:bg-surface-3 hover:text-foreground',
        )}
      >
        <ChevronRight className="h-4 w-4" strokeWidth={2} />
      </button>
    </div>
  )

  return (
    <section
      data-testid={disabled ? 'director-question-batch-readonly' : 'director-question-batch'}
      className="my-4 rounded-xl border border-border-subtle bg-surface-0 px-3 pb-3"
    >
      <QuestionCard
        key={currentQuestion.id}
        question={currentQuestion.question}
        options={currentQuestion.options}
        nodeRef={currentQuestion.nodeRef}
        byomConfiguration={currentQuestion.byomConfiguration}
        selectedOption={currentQuestion.selectedOption}
        onSelect={(option) => {
          onSelect?.(currentQuestion.id, option)
          questionBatchLog({
            event: 'question-batch:answer-selected',
            questionId: currentQuestion.id,
            current: currentIndex + 1,
            total,
          })
          window.setTimeout(() => advanceAfterAnswer(currentQuestion.id), 200)
        }}
        disabled={disabled}
        allowCustomInput={currentQuestion.allowCustomInput}
        customText={currentQuestion.customText}
        customSelected={currentQuestion.customSelected}
        onCustomTextChange={(text) => onCustomTextChange?.(currentQuestion.id, text)}
        onCustomInputFocus={() => onCustomInputFocus?.(currentQuestion.id)}
        onCustomSubmit={() => advanceAfterAnswer(currentQuestion.id)}
        canvasNodes={canvasNodes}
        headerAccessory={pager}
        forceListLayout
        showOptionNumbers
      />

      {currentQuestion.skipped && (
        <div
          role="status"
          data-testid="question-batch-skipped-status"
          className="px-1 pb-1 text-sm text-muted-foreground"
        >
          {t('questionBatch.skipped')}
        </div>
      )}

      {!disabled && (
        <div className="flex justify-end gap-2 pt-1">
          {currentIndex < total - 1 ? (
            <>
              {onSkip && (
                <button
                  type="button"
                  onClick={() => {
                    onSkip(currentQuestion.id)
                    questionBatchLog({
                      event: 'question-batch:question-skipped',
                      questionId: currentQuestion.id,
                      current: currentIndex + 1,
                      total,
                    })
                    goTo(currentIndex + 1)
                  }}
                  className="h-9 rounded-lg border border-input-border bg-surface-1 px-4 text-sm font-medium text-muted-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
                >
                  {t('questionBatch.skip')}
                </button>
              )}
              <button
                type="button"
                onClick={() => goTo(currentIndex + 1)}
                className="h-9 rounded-lg border border-input-border bg-surface-1 px-4 text-sm font-medium text-foreground transition-colors hover:border-input-border-hover hover:bg-surface-3 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20"
              >
                {t('questionBatch.next')}
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={() => {
                if (!canSubmit) return
                questionBatchLog({
                  event: 'question-batch:submit',
                  answerCount: questions.filter(hasAnswer).length,
                  skippedCount: questions.filter((question) => question.skipped).length,
                  unansweredCount: questions.filter((question) => !isComplete(question)).length,
                  total,
                })
                onSubmit?.()
              }}
              disabled={!canSubmit}
              title={!canSubmit ? t('questionBatch.completeEarlier') : undefined}
              className="h-9 rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {t('questionBatch.submit')}
            </button>
          )}
        </div>
      )}
    </section>
  )
}
